import {announce} from '@github-ui/aria-live'
import {setTitle as setDocumentTitle} from '@github-ui/document-metadata'
import {create} from '@github-ui/dotcom-schema/protobuf'
import {RepoIssuesDashboardPageInfoSchema} from '@github-ui/dotcom-schema/types/issues/v1/repo_issues_dashboard'
import {isFeatureEnabled} from '@github-ui/feature-flags'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports -- Transitional reuse of the existing milestone create control while its dialog remains Relay-backed.
import {CreateIssueButton} from '@github-ui/issue-create/CreateIssueButton'
import {FilteredListPageLayout} from '@github-ui/layout-primitives/FilteredListPageLayout'
import {entityFieldsMutationKey} from '@github-ui/optimistic-connection/pendingOperations'
import {useOptimisticData} from '@github-ui/optimistic-connection/useOptimisticData'
import {useReorderDisplayOrder} from '@github-ui/optimistic-connection/useReorderDisplayOrder'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {useRouteParams} from '@github-ui/react-core/future/use-route-params'
import {useRouteQuery, useSuspenseRouteQuery} from '@github-ui/react-core/future/use-route-query'
import {useQueryClient} from '@github-ui/react-query'
import {Link, SoftNavOnlyLink, useLocation, useNavigate} from '@github-ui/react-router'
import {AnalyticsWrapper as BaseAnalyticsWrapper} from '@github-ui/repo-issues-page-layout/AnalyticsWrapper'
import {RepoIssuesPageLayout} from '@github-ui/repo-issues-page-layout/RepoIssuesPageLayout'
import {useRepoIssuesSidebarLayoutEnabled} from '@github-ui/repo-issues-page-layout/useSidebarLayoutEnabled'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports -- Standalone plain-data renderer in the transitional milestone package.
import {MilestoneDetailData} from '@github-ui/repository-milestone/MilestoneDetailData'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports -- Reuse the milestone error UI during the gated migration.
import {MilestoneError} from '@github-ui/repository-milestone/MilestoneError'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports -- Standalone plain-data renderer in the transitional milestone package.
import {MilestoneTitleData} from '@github-ui/repository-milestone/MilestoneTitleData'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports -- This mutation hook is TanStack-backed despite its transitional package.
import {useUpdateMilestoneState} from '@github-ui/repository-milestone/use-update-milestone-state'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports -- Share the fallback text with the Relay milestone page.
import {LABELS} from '@github-ui/repository-milestone/labels'
import {useRuntimeEnvironment} from '@github-ui/ssr-utils/use-runtime-environment'
import {getListItemIdentity} from '@github-ui/shared-dashboard-lists/shared/helpers/item-identity'
import {getStateFromQuery} from '@github-ui/shared-dashboard-surface/helpers/open-closed-toggle'
// eslint-disable-next-line no-restricted-imports
import {useToastContext} from '@github-ui/toast/ToastContext'
import {ActionList, Banner, Button, PageHeader} from '@primer/react'
import {useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react'

import {IssuesListSurface} from '../components/IssuesListSurface'
import {RepoIssuesBulkJobInfo} from '../components/RepoIssuesBulkJobInfo'
import {useOptimisticRepoIssuesBulkUpdates} from '../helpers/repo-issues-bulk-optimistic-update'
import {createMilestoneRowNodeLoader} from '../hooks/repo-issues-row-node'
import {useIssuesDashboardLiveUpdates} from '../hooks/use-issues-dashboard-live-updates'
import {useMilestoneIssuesBulkActions} from '../hooks/use-milestone-issues-bulk-actions'
import {normalizedMilestonePage, useMilestoneIssuesPages} from '../hooks/use-milestone-issues-pages'
import {useRowNodeOverlay} from '../hooks/use-row-node-overlay'
import {useReprioritizeMilestoneIssue} from '../mutations/reprioritize-milestone-issue'
import {issuesLayoutRoute} from './issues-layout-route'
import {
  type MilestoneHeaderResponse,
  milestoneHeaderDocumentAdapter,
  milestoneHeaderQueryKey,
  milestoneHeaderToView,
  milestoneIssuesQuery,
  milestoneShowRoute,
} from './milestone-show-route'
import {useRenderSidebar} from './render-issues-sidebar'
import {syncMilestoneCaches} from './use-milestone-cache-sync'
import {useMilestoneEditRequest} from './use-optimistic-milestone-edit'

type MilestoneRepository = MilestoneHeaderResponse['repository']
type Milestone = MilestoneRepository['milestone']
type IssuePagesRefresh = {
  nameWithOwner: string
  number: number
  refresh: () => Promise<boolean>
}

type PendingLoadMore = {
  button: HTMLButtonElement | null
  targetPage: number
  previousIds: Set<string>
}

function findRowLink(container: HTMLElement | null, permalink: string | undefined) {
  if (!container || !permalink) return null
  for (const link of container.querySelectorAll('a[href]')) {
    if (link instanceof HTMLAnchorElement && link.getAttribute('href') === permalink) return link
  }
  return null
}

export function MilestoneShow() {
  return (
    <ErrorBoundary
      boundaryName="MilestoneShow"
      fallback={<MilestoneError title={LABELS.milestoneError} message={LABELS.milestoneErrorMessage} />}
    >
      <MilestoneShowPage />
    </ErrorBoundary>
  )
}

function MilestoneShowPage() {
  const {owner, repo, number: numberParam} = useRouteParams(milestoneShowRoute)
  const number = Number.parseInt(numberParam, 10)
  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const closed = searchParams.get('closed') === '1'
  const requestedQuery = searchParams.get('q')
  const initialPage = normalizedMilestonePage(searchParams.get('page'))
  const headerQuery = useRouteQuery(milestoneShowRoute, 'milestoneRoute')
  const mainQuery = useSuspenseRouteQuery(milestoneShowRoute, 'mainQuery')
  const secondaryQuery = useRouteQuery(milestoneShowRoute, 'secondary')
  const {data: layoutPayload} = useRouteQuery(issuesLayoutRoute, 'mainQuery')
  const optimisticHeader = useOptimisticData(headerQuery.data, {
    documentAdapter: milestoneHeaderDocumentAdapter,
    targets: [
      {
        queryKey: milestoneHeaderQueryKey(owner, repo, number),
        documentAdapter: milestoneHeaderDocumentAdapter,
        revalidate: true,
      },
    ],
  })
  const repository = milestoneHeaderToView(optimisticHeader).repository
  const milestone = repository.milestone
  const {addToast} = useToastContext()
  const queryClient = useQueryClient()
  // The mutation survives filter remounts, but must refresh the currently mounted list.
  const refreshIssuePagesRef = useRef<IssuePagesRefresh | null>(null)
  const {mutate: updateMilestoneState, isPending: isTogglePending} = useUpdateMilestoneState(async variables => {
    const [submittedOwner, submittedRepo] = variables.nameWithOwner.split('/')
    if (!submittedOwner || !submittedRepo) throw new Error('Invalid milestone repository')
    // Header settlement already revalidated and re-folded the confirmed state.
    await syncMilestoneCaches(queryClient, submittedOwner, submittedRepo, variables.number, {header: 'skip'})
    const issuePages = refreshIssuePagesRef.current
    if (issuePages?.nameWithOwner !== variables.nameWithOwner || issuePages.number !== variables.number) return
    const refreshed = await issuePages.refresh()
    if (!refreshed && refreshIssuePagesRef.current === issuePages) {
      announce('Failed to refresh issues. Please try again.')
    }
  })
  const toggleMilestone = useCallback(() => {
    if (
      isTogglePending ||
      queryClient.isMutating({
        mutationKey: entityFieldsMutationKey(),
        predicate: mutation => {
          const variables = mutation.state.variables
          if (!variables || typeof variables !== 'object' || !('operation' in variables)) return false
          const operation = variables.operation
          return !!operation && typeof operation === 'object' && 'id' in operation && operation.id === milestone.id
        },
      }) > 0
    ) {
      return
    }

    const state = milestone.closed ? 'OPEN' : 'CLOSED'
    announce(state === 'CLOSED' ? 'Closing milestone.' : 'Reopening milestone.')
    updateMilestoneState(
      {
        id: milestone.id,
        number: milestone.number,
        nameWithOwner: repository.nameWithOwner,
        state,
        operation: {type: 'setFields', id: milestone.id, fields: {closed: state === 'CLOSED'}},
      },
      {
        onSuccess: data => {
          const issuePages = refreshIssuePagesRef.current
          if (issuePages?.nameWithOwner === repository.nameWithOwner && issuePages.number === milestone.number) {
            announce(data.updateMilestone?.milestone?.closed ? 'Milestone closed.' : 'Milestone reopened.')
          }
        },
        onError: () => {
          // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
          addToast({type: 'error', message: 'Could not change Milestone state'})
        },
      },
    )
  }, [addToast, isTogglePending, milestone, queryClient, repository.nameWithOwner, updateMilestoneState])

  return (
    <MilestoneShowContent
      key={`${owner}/${repo}/${number}:${closed}:${requestedQuery ?? ''}:${initialPage}`}
      owner={owner}
      repo={repo}
      number={number}
      closed={closed}
      initialPage={initialPage}
      requestedQuery={requestedQuery}
      headerQuery={headerQuery}
      mainQuery={mainQuery}
      secondaryQuery={secondaryQuery}
      layoutPayload={layoutPayload}
      repository={repository}
      milestone={milestone}
      refreshIssuePagesRef={refreshIssuePagesRef}
      isTogglePending={isTogglePending}
      toggleMilestone={toggleMilestone}
    />
  )
}

function MilestoneShowContent({
  owner,
  repo,
  number,
  closed,
  initialPage,
  requestedQuery,
  headerQuery,
  mainQuery,
  secondaryQuery,
  layoutPayload,
  repository,
  milestone,
  refreshIssuePagesRef,
  isTogglePending,
  toggleMilestone,
}: {
  owner: string
  repo: string
  number: number
  closed: boolean
  initialPage: number
  requestedQuery: string | null
  headerQuery: ReturnType<typeof useRouteQuery<typeof milestoneShowRoute, 'milestoneRoute'>>
  mainQuery: ReturnType<typeof useSuspenseRouteQuery<typeof milestoneShowRoute, 'mainQuery'>>
  secondaryQuery: ReturnType<typeof useRouteQuery<typeof milestoneShowRoute, 'secondary'>>
  layoutPayload: ReturnType<typeof useRouteQuery<typeof issuesLayoutRoute, 'mainQuery'>>['data']
  repository: MilestoneRepository
  milestone: Milestone
  refreshIssuePagesRef: {current: IssuePagesRefresh | null}
  isTogglePending: boolean
  toggleMilestone: () => void
}) {
  const location = useLocation()
  const navigate = useNavigate()
  const editRequest = useMilestoneEditRequest(owner, repo, number)
  const isEditPending = editRequest?.status === 'pending'
  const data = mainQuery.data
  const secondaryData = secondaryQuery.data?.payload?.issuesIndexSecondaryData
  const query = requestedQuery || milestoneIssuesQuery(number, closed)
  const pathForQuery = useCallback(
    (nextQuery: string) => {
      const nextSearchParams = new URLSearchParams(location.search)
      const nextClosed = getStateFromQuery(nextQuery) === 'closed'
      nextSearchParams.delete('closed')
      nextSearchParams.delete('q')
      nextSearchParams.delete('page')
      if (nextClosed) nextSearchParams.set('closed', '1')
      if (nextQuery !== milestoneIssuesQuery(number, nextClosed)) nextSearchParams.set('q', nextQuery)
      const search = nextSearchParams.toString()
      return search ? `${location.pathname}?${search}` : location.pathname
    },
    [location.pathname, location.search, number],
  )
  const pages = useMilestoneIssuesPages({
    owner,
    repo,
    number,
    closed,
    requestedQuery,
    initialPage,
    firstMain: data,
    firstSecondary: secondaryQuery.data,
    firstUpdatedAt: mainQuery.dataUpdatedAt,
  })
  useLayoutEffect(() => {
    // Register at commit so mutation completion cannot use the previous filter before passive effects run.
    const issuePages = {nameWithOwner: repository.nameWithOwner, number, refresh: pages.refresh}
    refreshIssuePagesRef.current = issuePages
    return () => {
      if (refreshIssuePagesRef.current === issuePages) refreshIssuePagesRef.current = null
    }
  }, [number, pages.refresh, refreshIssuePagesRef, repository.nameWithOwner])
  const currentMain = pages.lastPage?.main ?? data
  const currentSecondary = pages.lastPage?.secondary?.payload?.issuesIndexSecondaryData ?? secondaryData
  const pageInfo = create(RepoIssuesDashboardPageInfoSchema, {
    currentPage: pages.currentPage,
    totalPages: currentMain.pageInfo?.totalPages ?? 1,
    totalCount: currentMain.pageInfo?.totalCount ?? 0,
    openCount: currentSecondary?.pageInfo?.openCount ?? currentMain.pageInfo?.openCount,
    closedCount: currentSecondary?.pageInfo?.closedCount ?? currentMain.pageInfo?.closedCount,
  })
  const rowNodeLoader = useMemo(() => createMilestoneRowNodeLoader(owner, repo, number), [number, owner, repo])
  const rowNodeQueryScope = `${owner}/${repo}:milestone=${number}`
  const rowNodeIssues = useRowNodeOverlay({
    rows: pages.rows,
    listUpdatedAt: pages.lastPage?.updatedAt ?? mainQuery.dataUpdatedAt,
    rowUpdatedAt: pages.rowUpdatedAt,
    loadRowNode: rowNodeLoader.loadRowNode,
    queryKeyScope: rowNodeQueryScope,
    removeMissingRows: true,
    onMissingRow: pages.reconcileMissingRow,
  })
  const issues = useOptimisticRepoIssuesBulkUpdates(rowNodeIssues)
  const issueRows = useMemo(() => issues.filter(issue => issue.itemType === 'issue'), [issues])
  const liveUpdates = useIssuesDashboardLiveUpdates(pages.rows, rowNodeQueryScope)
  const renderSidebar = useRenderSidebar(owner, repo, layoutPayload)
  const sidebarLayoutEnabled = useRepoIssuesSidebarLayoutEnabled()
  const listRef = useRef<HTMLDivElement>(null)
  const focusedRowRef = useRef<HTMLAnchorElement | null>(null)
  const pendingLoadMoreRef = useRef<PendingLoadMore | null>(null)
  const [retainLoadMore, setRetainLoadMore] = useState(false)

  useLayoutEffect(() => {
    if (focusedRowRef.current && !focusedRowRef.current.isConnected && document.activeElement === document.body) {
      listRef.current?.focus()
      focusedRowRef.current = null
    }
    const pending = pendingLoadMoreRef.current
    if (!pending || pages.currentPage < pending.targetPage) return
    pendingLoadMoreRef.current = null
    setRetainLoadMore(false)
    const appended = issues.find(issue => !pending.previousIds.has(getListItemIdentity(issue)))
    if (!appended || document.activeElement !== pending.button) return
    const rowLink = findRowLink(listRef.current, appended.permalink)
    if (!rowLink) return
    rowLink.focus({preventScroll: true})
    rowLink.scrollIntoView({block: 'nearest'})
  }, [issues, pages.currentPage])

  const {isBrowser} = useRuntimeEnvironment()
  const orderingEnabled =
    isBrowser &&
    repository.viewerCanUpdateMilestones &&
    (data.pageInfo?.totalCount ?? 1000) < 1000 &&
    (!closed || isFeatureEnabled('milestone_closed_issues_prioritization'))

  const orderingKey = JSON.stringify(issues.map(getListItemIdentity))
  const orderingIds = useMemo(() => JSON.parse(orderingKey) as string[], [orderingKey])
  const orderingDocument = useMemo(
    () => ({id: milestone.id, results: {nodes: orderingIds.map(id => ({id}))}}),
    [milestone.id, orderingIds],
  )
  const optimisticOrdering = useOptimisticData(orderingDocument, {
    connections: [{ownerId: milestone.id, connectionPath: ['results']}],
    enabled: orderingEnabled,
  })
  const {displayNodes: displayedOrderingNodes, applyLocalReorder} = useReorderDisplayOrder(
    optimisticOrdering.results.nodes,
    orderingEnabled,
  )
  const rowsById = useMemo(() => new Map(issues.map(row => [getListItemIdentity(row), row])), [issues])
  const orderedRows =
    displayedOrderingNodes?.flatMap(item => {
      const row = rowsById.get(item.id)
      return row ? [row] : []
    }) ?? []

  const handleOrderingError = useCallback(() => {
    announce('Failed to reorder milestone item. Please try again.', {assertive: true})
  }, [])
  const {reprioritize, isPending: isOrderingPending} = useReprioritizeMilestoneIssue({
    owner,
    repo,
    number,
    milestoneId: milestone.id,
    milestoneUpdatedAt: milestone.updatedAt,
    itemIds: optimisticOrdering.results.nodes.map(item => item.id),
    closed,
    requestedQuery,
    onError: handleOrderingError,
  })

  const onDrop = useCallback(
    ({
      dragMetadata,
      dropMetadata,
      isBefore,
    }: Parameters<NonNullable<Parameters<typeof IssuesListSurface>[0]['ordering']>['onDrop']>[0]) => {
      if (dragMetadata.id === dropMetadata.id) return
      applyLocalReorder(dragMetadata.id, dropMetadata.id, isBefore)
      reprioritize({dragItemId: dragMetadata.id, dropItemId: dropMetadata.id, isBefore})
    },
    [applyLocalReorder, reprioritize],
  )

  useEffect(() => {
    setDocumentTitle(`${milestone.title} · Milestone #${milestone.number} · ${repository.nameWithOwner}`)
  }, [milestone.number, milestone.title, repository.nameWithOwner])

  const refreshIssues = useCallback(async () => {
    const [header, refreshed] = await Promise.all([headerQuery.refetch(), pages.refresh()])
    if (header.isError || !refreshed) {
      announce('Failed to refresh issues. Please try again.')
      return {isError: true, error: header.error ?? new Error('Unable to refresh milestone issues.')}
    }
    return {isError: false, error: null}
  }, [headerQuery, pages])
  const loadMore = async (button: HTMLButtonElement | null = null) => {
    const pending: PendingLoadMore | null = button && {
      button,
      targetPage: pages.currentPage + 1,
      previousIds: new Set(issues.map(getListItemIdentity)),
    }
    if (pending) {
      pendingLoadMoreRef.current = pending
      setRetainLoadMore(true)
    }
    if (await pages.loadMore()) announce('More issues loaded.')
    else if (pending && pendingLoadMoreRef.current === pending) {
      pendingLoadMoreRef.current = null
      setRetainLoadMore(false)
    }
  }

  const viewerCanBulkUpdate = layoutPayload?.canBulkUpdate === true
  const {
    bulkJobId,
    clearBulkJobId,
    selection,
    bulkActionError,
    clearBulkActionError,
    onBulkJobCompleted,
    onBulkJobError,
  } = useMilestoneIssuesBulkActions({
    owner,
    repo,
    repositoryId: repository.id,
    viewerId: layoutPayload?.currentUser?.id ?? '',
    milestoneId: milestone.id,
    view: closed ? 'closed' : 'open',
    rows: issues,
    issueRows,
    viewerCanBulkUpdate,
    hasProjectsEnabled: layoutPayload?.hasProjectsEnabled ?? false,
    issueTypesEnabled: layoutPayload?.issueTypesEnabled ?? false,
    refreshIssues,
  })

  const actions = useMilestoneControls({
    owner: repository.owner.login,
    repo: repository.name,
    milestoneNumber: milestone.number,
    milestoneTitle: milestone.title,
    closed: milestone.closed,
    viewerCanUpdateMilestones: repository.viewerCanUpdateMilestones,
    isPending: isTogglePending || isEditPending,
    onToggle: toggleMilestone,
  })

  return (
    <BaseAnalyticsWrapper category="Repository Milestone Show" appName="issues">
      <RepoIssuesPageLayout
        dialogTitle="Milestones"
        title={sidebarLayoutEnabled ? milestone.title : undefined}
        contextArea={
          sidebarLayoutEnabled ? (
            <PageHeader.ParentLink hidden={false} href={`/${repository.nameWithOwner}/milestones`}>
              Back to milestones
            </PageHeader.ParentLink>
          ) : undefined
        }
        actions={
          sidebarLayoutEnabled ? (
            <FilteredListPageLayout.HeaderActions
              menuAriaLabel="More milestone actions"
              actions={actions.menuActions}
            />
          ) : undefined
        }
        renderSidebar={renderSidebar}
        initialExpanded={layoutPayload?.sidebarExpanded}
      >
        {!sidebarLayoutEnabled && (
          <MilestoneTitleData title={milestone.title} nameWithOwner={repository.nameWithOwner}>
            {actions.inline}
          </MilestoneTitleData>
        )}
        {editRequest?.status === 'error' && (
          <Banner
            className="mb-3"
            variant="critical"
            title="Milestone changes could not be saved"
            description={
              editRequest.error === LABELS.milestoneEditError
                ? 'Your draft has been kept. Check your connection and try again.'
                : editRequest.error
            }
            primaryAction={
              <Button as={SoftNavOnlyLink} to={`/${owner}/${repo}/milestones/${number}/edit`}>
                Edit and retry
              </Button>
            }
          />
        )}
        <MilestoneDetailData
          milestone={milestone}
          optimisticDescription={
            isEditPending && !editRequest.variables?.state
              ? (editRequest.variables?.input.description ?? '')
              : undefined
          }
        />
        <RepoIssuesBulkJobInfo
          jobId={bulkJobId}
          enabled={viewerCanBulkUpdate}
          clearJobId={clearBulkJobId}
          refreshIssues={refreshIssues}
          onCompleted={onBulkJobCompleted}
          onError={onBulkJobError}
        />
        {bulkActionError ? (
          <Banner className="mt-3" variant="critical" title={bulkActionError} onDismiss={clearBulkActionError} />
        ) : null}
        <div
          ref={listRef}
          role="region"
          aria-label="Milestone issues"
          tabIndex={-1}
          onFocusCapture={event => {
            const link = event.target instanceof Element ? event.target.closest('a[href]') : null
            focusedRowRef.current =
              link instanceof HTMLAnchorElement && issues.some(issue => issue.permalink === link.getAttribute('href'))
                ? link
                : null
          }}
        >
          {pages.error && (
            <Banner
              variant="critical"
              title={pages.errorKind === 'refresh' ? 'Unable to refresh issues.' : 'Unable to load more issues.'}
              description={pages.error}
              primaryAction={
                <Button onClick={() => void (pages.errorKind === 'refresh' ? refreshIssues() : loadMore())}>
                  Retry
                </Button>
              }
            />
          )}
          <IssuesListSurface
            response={{...currentMain, pageInfo}}
            secondaryResults={currentSecondary?.results}
            routeAdapter={{
              scopeIdentity: `repository:${owner}/${repo}:milestone:${number}:${closed ? 'closed' : 'open'}:${query}`,
              query,
              page: pages.currentPage,
              fetch: ({query: nextQuery}) => void navigate(pathForQuery(nextQuery)),
              getPageHref: () => pathForQuery(query),
              getQueryHref: nextQuery => pathForQuery(nextQuery),
            }}
            issuesOverride={orderedRows}
            beforeList={liveUpdates}
            paginationMode="none"
            showSorting={false}
            showDensityToggle={false}
            ordering={
              orderingEnabled ? {onDrop, isPending: isOrderingPending, fullWidthUnreadIndicator: true} : undefined
            }
            selection={selection}
          />
          {(pages.hasMore || retainLoadMore) && (
            <div className="d-flex flex-justify-center mt-3" data-testid="milestone-load-more">
              <Button
                variant="invisible"
                onClick={event => {
                  if (!pages.isLoading) {
                    void loadMore(event.detail === 0 ? event.currentTarget : null)
                  }
                }}
                disabled={pages.isLoading && !retainLoadMore}
                aria-disabled={pages.isLoading || undefined}
                aria-live="polite"
              >
                {pages.isLoading && !pages.isRefreshing ? 'Loading...' : 'Load more'}
              </Button>
            </div>
          )}
        </div>
      </RepoIssuesPageLayout>
    </BaseAnalyticsWrapper>
  )
}

function useMilestoneControls({
  owner,
  repo,
  milestoneNumber,
  milestoneTitle,
  closed,
  viewerCanUpdateMilestones,
  isPending,
  onToggle,
}: {
  owner: string
  repo: string
  milestoneNumber: number
  milestoneTitle: string
  closed: boolean
  viewerCanUpdateMilestones: boolean
  isPending: boolean
  onToggle: () => void
}) {
  const navigate = useNavigate()
  const navigateToMilestone = useCallback(
    (url: string) => {
      const milestoneParam = `milestone=${encodeURIComponent(milestoneTitle)}`
      navigate(url.includes('?') ? `${url}&${milestoneParam}` : `${url}?${milestoneParam}`)
    },
    [milestoneTitle, navigate],
  )
  const editUrl = `/${owner}/${repo}/milestones/${milestoneNumber}/edit`
  const renderEditButton = () => (
    <Button as={Link} to={editUrl}>
      Edit
    </Button>
  )
  const renderToggleButton = () => (
    <Button
      onClick={() => {
        if (!isPending) onToggle()
      }}
      aria-disabled={isPending || undefined}
      inactive={isPending}
    >
      {closed ? 'Reopen Milestone' : 'Close Milestone'}
    </Button>
  )
  const renderCreateIssueButton = () => (
    <CreateIssueButton
      label="New issue"
      navigate={navigateToMilestone}
      optionConfig={{
        showRepositoryPicker: false,
        issueCreateArguments: {repository: {owner, name: repo}},
        showFullScreenButton: true,
        navigateToFullScreenOnTemplateChoice: true,
        canBypassTemplateSelection: true,
      }}
    />
  )
  const inline = (
    <>
      {viewerCanUpdateMilestones && (
        <>
          {renderEditButton()}
          {renderToggleButton()}
        </>
      )}
      {renderCreateIssueButton()}
    </>
  )
  const menuActions = [
    ...(viewerCanUpdateMilestones
      ? [
          {
            key: 'edit-milestone',
            renderInline: renderEditButton,
            renderMenuItem: () => (
              <ActionList.LinkItem as={Link} to={editUrl}>
                Edit
              </ActionList.LinkItem>
            ),
          },
          {
            key: 'toggle-milestone-state',
            renderInline: renderToggleButton,
            renderMenuItem: () => (
              <ActionList.Item disabled={isPending} onSelect={onToggle}>
                {closed ? 'Reopen Milestone' : 'Close Milestone'}
              </ActionList.Item>
            ),
          },
        ]
      : []),
    {
      key: 'new-issue',
      alwaysInline: true,
      renderInline: renderCreateIssueButton,
      renderMenuItem: () => null,
    },
  ]
  return {inline, menuActions}
}
